import apiClient from '@/lib/api';
import { Place } from '@/types/place';
import { ApiResponse } from '@/types/api';

export interface FavoriteResponse extends ApiResponse<{ placeId: string }> {}

export const favoritesApi = {
  /**
   * Add a place to user's favorites.
   */
  add: (userId: string, placeId: string): Promise<FavoriteResponse> => {
    return apiClient.post(`/api/users/${userId}/favorites`, {
      placeId,
    }).then(res => res.data);
  },

  /**
   * Remove a place from user's favorites.
   */
  remove: (userId: string, placeId: string): Promise<void> => {
    return apiClient.delete(`/api/users/${userId}/favorites/${placeId}`).then(() => undefined);
  },

  /**
   * Get all favorite places for a user.
   * Backend returns: { data: { Favorites: [{ placeId: Place, dateAdded: string }] } }
   * We extract the placeId from each favorite and return Place[]
   */
  getUserFavorites: (userId: string): Promise<Place[]> => {
    return apiClient.get(`/api/users/${userId}/favorites`).then(res => {
      const favorites = res.data.data?.Favorites || [];
      // Extract placeId from each favorite object
      return favorites.map((fav: any) => ({
        ...fav.placeId,
        dateAdded: fav.dateAdded, // Preserve dateAdded for display
      })) as Place[];
    });
  },

  /**
   * @deprecated Use isPlaceInFavorites with cached favorites array instead.
   * This function fetches all favorites just to check one place, which is inefficient.
   * 
   * Check if a place is favorited by a user.
   * Returns boolean indicating favorite status.
   */
  isFavorite: async (userId: string, placeId: string): Promise<boolean> => {
    const favorites = await favoritesApi.getUserFavorites(userId);
    return favorites.some(fav => fav._id === placeId || (fav as any).placeId === placeId);
  },
};

/**
 * Check if a place is in the favorites array (uses cached data, no API call).
 * This is the preferred method for checking favorite status when you already have the favorites list.
 */
export const isPlaceInFavorites = (placeId: string, favorites: Place[]): boolean => {
  return favorites.some(fav => fav._id === placeId || (fav as any).placeId === placeId);
};
